import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { Alert } from '@neo/primitives/alert'
import { Separator } from '@neo/primitives/separator'
import { ReactComponent as GoogleIcon } from '@shared/assets/icons/ic-google-color.svg'
import type { ApiResponse, LoginFormData } from '@shared/types'
import type { AxiosError } from 'axios'
import dayjs from 'dayjs'
import React, { useEffect, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, Navigate, useNavigate, useSearchParams } from 'router-v6'
import { RECAPTCHA_SITE_KEY } from 'v2/configs/constants'
import { getDefaultRoute } from 'v2/configs/routes'
import { queryClient } from 'v2/configs/rq-setup'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import type { RLoginResponse } from 'v2/types/api/auth'
import { getSafeRedirectUrl } from 'v2/utils/url-validation'
import { z } from 'zod'
import { AuthLayout } from '../../shared/layouts/auth'
import { useLoginMutation, useSsoAuthorizeMutation } from './queries'

type LoginResponse = ApiResponse<RLoginResponse>

// Constants for magic values
const OTP_METHOD = {
  SMS: 'sms',
} as const

const AUTH_TYPE = {
  LOGIN: false,
  SIGNUP: true,
} as const

// Helper function to create base navigation state
const createBaseNavigationState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  email: formData.email,
  password: formData.password,
  accountID: response.data.account_id,
  aomUUID: response.data.aom_uuid,
  isSignup: AUTH_TYPE.LOGIN,
  originalLoginResponse: response.data,
  region: response.data.region,
  nextUrl,
})

// Helper function to create two-factor verify navigation state
const createTwoFactorVerifyState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  ...createBaseNavigationState(formData, response, nextUrl),
  phoneNumber: response.data.phone_number,
  sessionUuid: response.data.session_uuid,
  method: OTP_METHOD.SMS,
  isAuthenticatorDefault: response.data.authenticator_status.is_default,
  isAuthenticatorEnabled: response.data.authenticator_status.is_present,
  otpSentTime: dayjs().toISOString(),
})

// Helper function to create two-factor auth navigation state
const createTwoFactorAuthState = (
  formData: LoginFormData,
  response: LoginResponse,
  nextUrl: string | null,
) => ({
  ...createBaseNavigationState(formData, response, nextUrl),
  verificationPending: true,
})

const fields: GenericFieldConfig<LoginFormData> = [
  {
    name: 'email',
    componentType: 'input',
    zodValidation: z
      .string()
      .refine((val) => val.length > 0, { message: 'Email is required' })
      .refine((val) => val.length === 0 || z.string().email().safeParse(val).success, {
        message: 'Valid email is required',
      }),
    label: 'Work Email',
    inputProps: {
      placeholder: 'jane@company.com',
      inputClassName: 'py-3 h-[44px]',
      autoComplete: 'username',
      'data-testid': 'input-email',
    },
  },
  {
    name: 'password',
    componentType: 'inputPassword',
    zodValidation: z.string().min(1, 'Password is required'),
    label: (
      <div className="flex items-center justify-between gap-2">
        <span>Password</span>
        <Link to="/forgot-password" className="text-xs-400 text-secondary hover:text-primary">
          Forgot your password?
        </Link>
      </div>
    ),
    inputProps: {
      inputClassName: 'py-3 h-[44px]',
      autoComplete: 'current-password',
      placeholder: 'Enter your password',
      'data-testid': 'input-password',
    },
  },
]

export const Login = () => {
  const recaptchaRef = React.useRef() as React.MutableRefObject<ReCAPTCHA>
  const { mutate: loginUser, isPending, error } = useLoginMutation(recaptchaRef)
  const { mutate: ssoAuthorize } = useSsoAuthorizeMutation()
  const { userAuthenticated } = useAuth()
  const defaultValueRef = useRef<LoginFormData>({ email: '', password: '' })
  const [searchParams] = useSearchParams()
  const nextUrl = getSafeRedirectUrl(searchParams.get('next_url'))
  const navigate = useNavigate()

  useEffect(() => {
    if (!userAuthenticated) {
      queryClient.removeQueries()
      queryClient.clear()
    }
  }, [userAuthenticated])

  // If the user is already authenticated, redirect them to the inbox.
  if (userAuthenticated) {
    if (nextUrl) {
      return <Navigate to={nextUrl} />
    }

    return <Navigate to={getDefaultRoute()} />
  }

  // Navigate to two-factor verification screen for login
  const navigateToTwoFactorVerify = (formData: LoginFormData, response: LoginResponse) => {
    const navigationState = createTwoFactorVerifyState(formData, response, nextUrl)
    navigate('/two-factor-verify', { state: navigationState })
  }

  // Navigate to two-factor auth screen
  const navigateToTwoFactorAuth = (formData: LoginFormData, response: LoginResponse) => {
    const navigationState = createTwoFactorAuthState(formData, response, nextUrl)
    navigate('/two-factor-auth', { state: navigationState })
  }

  const handleLogin = (formData: LoginFormData) => {
    loginUser(formData, {
      onSuccess: (response) => {
        // Check if 2FA is required
        if (response.data.two_fa_enabled && !response.data.redirect_two_fa_auth) {
          navigateToTwoFactorVerify(formData, response)
        } else if (response.data.two_fa_enabled && response.data.redirect_two_fa_auth) {
          navigateToTwoFactorAuth(formData, response)
        }
        // If no 2FA required, the normal login flow will proceed automatically
        // The login mutation will handle authentication and navigation
      },
    })
  }

  return (
    <div>
      <AuthLayout
        pageTitle="Login to your account"
        title="Login to your account"
        description="Welcome back! Manage and monitor your AI agents."
        // footer={
        //   <Link
        //     to="/signup"
        //     className="text-center text-p-400 text-muted-foreground hover:text-primary"
        //   >
        //     Don&apos;t have an account? Sign up
        //   </Link>
        // }
      >
        {error && (
          <Alert
            variant="destructive"
            content={getErrorText(error as AxiosError<ApiResponse<unknown>>)}
          />
        )}
        <GenericForm
          id="login-form"
          defaultValues={defaultValueRef.current}
          fields={fields}
          onSubmit={handleLogin}
          className="grid gap-4"
          isSubmitting={isPending}
          submitText="Login"
          includeSubmitButton={false}
        />
        <div className="flex flex-col gap-4">
          <FormSubmitButton formId="login-form" className="w-full" size="lg" loading={isPending}>
            Login
          </FormSubmitButton>
        </div>
        <div className="flex items-center gap-4">
          <Separator className="flex-1" />
          <span className="text-muted-foreground text-sm font-medium">OR</span>
          <Separator className="flex-1" />
        </div>
        <Button
          variant="outline"
          className="flex justify-start p-0 h-14 text-left"
          onClick={() => ssoAuthorize({ sso_type: 'google', next: nextUrl || undefined })}
        >
          <span className="size-14 flex items-center justify-center">
            <GoogleIcon />
          </span>
          <Separator orientation="vertical" className="h-full" />
          <span className="ml-4 text-p-500">Login with Google</span>
        </Button>
        <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-center sm:flex-nowrap">
          <span className="text-xs-400 text-secondary">
            By continuing, you agree to Plivo&apos;s
          </span>
          <Link
            to="https://www.plivo.com/legal/tos/"
            className="text-xs-400 text-primary underline"
            data-testid="link-terms-of-service"
            target="_blank"
            rel="noopener"
          >
            ToS
          </Link>
          <span className="text-xs-400 text-secondary">&</span>
          <Link
            to="https://www.plivo.com/legal/privacy/"
            className="text-xs-400 text-primary underline"
            data-testid="link-privacy-policy"
            target="_blank"
            rel="noopener"
          >
            Privacy Policy
          </Link>
        </div>
        <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
      </AuthLayout>
    </div>
  )
}
